import React, { useEffect } from 'react';
import { UI_BODY_SEMIBOLD_SM } from "oolib";
import FilterModule from "@/components/discovery/FilterModule";
import { useWidgetFilters } from './useWidgetFilters';

/**
 * Reusable component that renders the filter section for widget config forms
 *
 * @param {Object} props
 * @param {Object} props.widgetConfig - The widget configuration object
 * @param {Function} props.updateWidgetConfig - Function to update the widget config
 * @param {number} props.widgetIndex - Index of the widget being edited
 * @param {Array<string>} props.contentTypesForFilters - Array of content types for generating filters (optional if selectedTpls is provided)
 * @param {Array} props.selectedTpls - Pre-selected templates to use for filters (optional, takes precedence over contentTypesForFilters)
 * @param {string} props.configPath - Path in widget config where filters are stored (default: "props.widgetFilters")
 * @param {string} props.label - Label text for the filter section (default: "Filters")
 * @param {boolean} props.shouldShowFilters - Whether to show the filters section (default: true if hasContentTypes)
 * @param {Function} props.onHasFilterConfigs - Callback reporting whether filter configs are available
 * @param {boolean} props.hasDimensions - Whether any dimension is configured (from useWidgetFilterControls)
 */
export const WidgetFilters = ({
  widgetConfig,
  updateWidgetConfig,
  widgetIndex,
  contentTypesForFilters = [],
  selectedTpls,
  configPath,
  label = "Filters",
  shouldShowFilters,
  addFilterAnchorEl,
  addFilterLightboxOpen,
  onAddFilterLightboxOpenChange,
  onClearFilters,
  onHasFiltersToClear,
  onHasFilterConfigs,
  hasDimensions,
}) => {
  const {
    filterConfigs,
    localFilters,
    handleFilterChange,
    hasContentTypes,
    contentTypes
  } = useWidgetFilters({
    widgetConfig,
    updateWidgetConfig,
    widgetIndex,
    contentTypesForFilters,
    selectedTpls,
    configPath
  });

  // Report filter config availability to parent (useWidgetFilterControls)
  useEffect(() => {
    const hasConfigs = !!filterConfigs && filterConfigs.length > 0;
    onHasFilterConfigs?.(hasConfigs);
    return () => onHasFilterConfigs?.(false);
  }, [filterConfigs, onHasFilterConfigs]);

  // If explicitly told not to show, respect that
  if (shouldShowFilters === false) return null;

  // Dimensions incomplete (e.g. heat map with one axis, sankey with one step):
  // the section header's "+" is disabled with a tooltip, so render nothing here.
  // Rendering FilterModule without an anchor would make it draw its own
  // "Filters" title and "Add Filter" pill — a second header under ours.
  if (hasDimensions === false) return null;

  // No dimensions configured at all — nothing to show
  if (!hasContentTypes && !hasDimensions) return null;

  // Dimensions exist but no filter configs generated (e.g. annotation-only dimensions, or no filterable fields)
  if (!filterConfigs || filterConfigs.length === 0) {
    return (
      <div style={{ padding: "0.8rem 0" }}>
        <UI_BODY_SEMIBOLD_SM style={{ color: "var(--grey500)" }}>
          No filter combination present
        </UI_BODY_SEMIBOLD_SM>
      </div>
    );
  }

  return (
    <div>
      <FilterModule
        usesAnnoFilters={true}
        key={`filter-module-${widgetIndex}`}
        filterConfig={filterConfigs}
        activeFilters={localFilters}
        setActiveFilters={handleFilterChange}
        contentTypes={contentTypes}
        orientation="sidebar"
        style={{ padding: "1.4rem 0" }}
        showClearAllButton={false}
        skipLocalFilterSave={true}
        addFilterAnchorEl={addFilterAnchorEl}
        addFilterLightboxOpen={addFilterLightboxOpen}
        onAddFilterLightboxOpenChange={onAddFilterLightboxOpenChange}
        onClearFilters={onClearFilters}
        onHasFiltersToClear={onHasFiltersToClear}
        disableInlinePadding={!!addFilterAnchorEl}
      />
    </div>
  );
};

